import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { range } from 'lodash';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  ContractorService,
  Utilities,
  WorkflowContextService,
  WorkflowService,
  SecurityService,
  SystemService
} from '../../../../services';

import {
  WorkflowType,
  ContractorType,
  ContractorTypeRequirement,
  WorkflowCategory
} from '../../../../models';

import { ConditionTarget } from 'src/app/components/system/condition-builder/condition-builder.model';
import { DataEntity } from 'src/app/models/data-entities';

import {
  NewContractorTypeState,
  INITIAL_STATE,
  EXPIRATION_MODES,
  EXPIRATION_OPTIONS,
  WORKFLOW_TYPES
} from './new-contractor-type.model';

import { WorkflowValidationService } from 'src/app/services/workflow-validation.service';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WorkflowValidityModalComponent } from 'src/app/components/workflow/workflow-validity-modal/workflow-validity-modal.component';

import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';

// TAGS
import { ContractorTypeTag } from 'src/app/models/contractor-type-tag';
import { ItemTag } from 'src/app/models/item-tag';
import { Tag } from 'src/app/models/tag';

@Component({
  selector: 'app-new-contractor-type',
  standalone: false,
  templateUrl: './new-contractor-type.component.html',
  styleUrls: ['./new-contractor-type.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NewContractorTypeComponent implements OnInit, OnDestroy {
  vm: NewContractorTypeState = {
    ...INITIAL_STATE,
    contractorTypeTags: [],
    customExpirationCriteria: [] as ConditionTarget[]
  } as any;

  testRegisterLoading = false;
  testRenewalLoading = false;
  showTestWorkflow = false;
  routerSub: Subscription;

  registerWorkflowCategories: WorkflowCategory[] = [];
  renewalWorkflowCategories: WorkflowCategory[] = [];
  registerCategoriesEnabled = false;
  renewalCategoriesEnabled = false;

  validity: { register: any[]; renewal: any[] } = {
    register: [],
    renewal: []
  };

  validationInfo: {
    register: { class: string; icon: string; text: string };
    renewal: { class: string; icon: string; text: string };
  } = {
    register: { class: '', icon: 'info', text: '' },
    renewal: { class: '', icon: 'info', text: '' }
  };

  private readonly overlayTopSpacing = 0;
  private readonly overlayBottomSpacing = 100;
  private readonly defaultOverlayHeight = 720;
  private readonly minimumOverlayHeight = 720;

  overlayOffsetTop = 0;
  overlayAvailableHeight = '720px';

  constructor(
    private _fb: UntypedFormBuilder,
    private _contractorSvc: ContractorService,
    private _contextSvc: WorkflowContextService,
    private _workflowSvc: WorkflowService,
    private _systemSvc: SystemService,
    private route: ActivatedRoute,
    private router: Router,
    private _ref: ChangeDetectorRef,
    private _securitySvc: SecurityService,
    private _workflowValidation: WorkflowValidationService,
    private modalService: NgbModal,
    private _toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initDateHelpers();
    this.initForm();
    this.loadWorkflows();

    const id = this.route.snapshot.paramMap.get('contractorTypeId');
    this.vm.isEditMode = !!id;

    this.syncWorkflowChildState();

    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.syncWorkflowChildState();
      });

    //this.ensureExpirationOptionsIncludeCustomFormula();
    setTimeout(() => {
      this.updateOverlayOffset();
      this._ref.detectChanges();
    });

    if (this.vm.isEditMode) {
      this.vm.loadingData = true;
      this.loadFullContractorType(id as string);
    } else {
      this.vm.loadingData = false;
      this.initializeNewMode();
    }
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  private toastSuccess(message: string, title = 'Success!') {
    this._toastr.success(message, title, { onActivateTick: true });
  }

  private toastError(err: any, fallbackMessage: string, title?: string) {
    const isServerError =
      err instanceof HttpErrorResponse ||
      (err?.name && err.name === 'HttpErrorResponse');

    const toastTitle =
      title || (isServerError ? 'Server Error!' : 'System Error!');

    const serverMsg = err?.error?.message || err?.error?.title || err?.message;
    const msg = serverMsg ? String(serverMsg) : fallbackMessage;

    this._toastr.error(msg, toastTitle, { onActivateTick: true });
  }

  sortRequirements = () => {
    if (!this.vm.fullEditingObject || !this.vm.fullEditingObject.requirements)
      return;
    this.vm.contractorRequirements = this.vm.fullEditingObject.requirements.filter(
      req => req.isContractorRequirement
    );
    this.vm.registrationRequirements = this.vm.fullEditingObject.requirements.filter(
      req => !req.isContractorRequirement
    );
  };

  buildEntities = (): Observable<DataEntity[]> => {
    if (!this.vm.fullEditingObject?.registerWorkflow) {
      return new Observable<DataEntity[]>(sub => sub.complete());
    }
    return this._workflowSvc.getDataEntities(
      this.vm.fullEditingObject.registerWorkflow,
      null,
      null,
      [
        WorkflowService.ACTIVITIES.ContractorInfo,
        WorkflowService.ACTIVITIES.ContractorRegistrationInfo
      ]
    );
  };

  loadRequirementEntities = () => {
    if (!this.vm.fullEditingObject?.registerWorkflow) return;

    this.buildEntities().subscribe({
      next: entities => {
        this.vm.contractorEntities = entities.filter(
          e =>
            e.parent.activityType === WorkflowService.ACTIVITIES.ContractorInfo
        );

        this.vm.availableContractorEntities = this.vm.contractorEntities.filter(
          ce =>
            !this.vm.contractorRequirements.some(
              cr => cr.templateCode === ce.templateCode
            )
        );

        this.vm.registrationEntities = entities.filter(
          e =>
            e.parent.activityType ===
            WorkflowService.ACTIVITIES.ContractorRegistrationInfo
        );

        this.vm.availableRegistrationEntities = this.vm.registrationEntities.filter(
          re =>
            !this.vm.registrationRequirements.some(
              rr => rr.templateCode === re.templateCode
            )
        );

        this._ref.detectChanges();
      }
    });
  };

  addNewContractorRequirement = () => {
    this.vm.newContractorRequirement = new ContractorTypeRequirement();
    this.vm.newContractorRequirement.id = Utilities.generateId();
    this.vm.newContractorRequirement.isContractorRequirement = true;
    this._ref.detectChanges();
  };

  addNewRegistrationRequirement = () => {
    this.vm.newRegistrationRequirement = new ContractorTypeRequirement();
    this.vm.newRegistrationRequirement.id = Utilities.generateId();
    this.vm.newRegistrationRequirement.isContractorRequirement = false;
    this._ref.detectChanges();
  };

  newRequirementApplied = (requirement: ContractorTypeRequirement) => {
    if (!this.vm.fullEditingObject.requirements)
      this.vm.fullEditingObject.requirements = [];
    this.vm.fullEditingObject.requirements.push(requirement);
    this.vm.newContractorRequirement = null;
    this.vm.newRegistrationRequirement = null;
    this.sortRequirements();
    this.loadRequirementEntities();
  };

  newContractorRequirementCancelled = () => {
    this.vm.newContractorRequirement = null;
    this._ref.detectChanges();
  };

  newRegistrationRequirementCancelled = () => {
    this.vm.newRegistrationRequirement = null;
    this._ref.detectChanges();
  };

  removeRequirement = (requirement: ContractorTypeRequirement) => {
    const index = this.vm.fullEditingObject.requirements.indexOf(requirement);
    if (index > -1) {
      this.vm.fullEditingObject.requirements.splice(index, 1);
      this.sortRequirements();
      this.loadRequirementEntities();
    }
  };

  private ensureDefaultRequirementActivities = () => {
    if (!this.vm.fullEditingObject) return;
    const obj = this.vm.fullEditingObject as any;
    if (
      !obj.registerRequirementActivities ||
      Object.keys(obj.registerRequirementActivities).length === 0
    ) {
      const id = Utilities.generateId();
      obj.registerRequirementActivities = {
        [id]: {
          id,
          activityType: 'Form',
          model: {
            screenName: 'General Requirements',
            formLayoutModel: { rows: [] }
          }
        } as any
      };
    }
    if (
      !obj.renewRequirementActivities ||
      Object.keys(obj.renewRequirementActivities).length === 0
    ) {
      const id = Utilities.generateId();
      obj.renewRequirementActivities = {
        [id]: {
          id,
          activityType: 'Form',
          model: {
            screenName: 'General Requirements',
            formLayoutModel: { rows: [] }
          }
        } as any
      };
    }
  };

  private initDateHelpers = () => {
    this.vm.monthOptions = range(1, 13, 1).map(m => ({
      label: moment()
        .month(m - 1)
        .format('MMMM'),
      value: m
    }));
    this.vm.dayOptions = range(1, 29).map(d => ({
      label: d.toString(),
      value: d
    }));
  };

  private initForm = () => {
    this.vm.form = this._fb.group({
      name: ['', Validators.required],
      description: [''],
      registerWorkflowId: [null, Validators.required],
      renewWorkflowId: [null, Validators.required],
      expirationOption: [EXPIRATION_MODES.INFINITE],
      termLength: [null],
      visibleToPublic: [true]
    });

    this.vm.settingsForm = this._fb.group({
      registerAppDescription: [''],
      registerAppDescSearchEntities: [''],
      registrationNumberPrefix: [''],
      registrationStartingNumber: [1],
      registerTotalLength: [null],
      registerSearchEntities: [''],
      registerAdvancedPrefixEditor: [false],
      registerAutoReset: [false],
      registerResetMonth: [{ value: null, disabled: true }],
      registerResetDay: [{ value: null, disabled: true }],
      registrationEnableAutoVoid: [false],

      renewalAppDescription: [''],
      renewalAppDescSearchEntities: [''],
      renewalNumberPrefix: [''],
      renewalStartingNumber: [1],
      renewalTotalLength: [null],
      renewalSearchEntities: [''],
      renewalAdvancedPrefixEditor: [false],
      renewalAutoReset: [false],
      renewalResetMonth: [{ value: null, disabled: true }],
      renewalResetDay: [{ value: null, disabled: true }],
      renewalEnableAutoVoid: [false],

      registrationAutoVoid: this._fb.group({
        goBeyondActivity: ['', Validators.required],
        daysUntilVoid: ['', [Validators.required, Validators.min(1)]]
      }),
      renewalAutoVoid: this._fb.group({
        goBeyondActivity: ['', Validators.required],
        daysUntilVoid: ['', [Validators.required, Validators.min(1)]]
      })
    });

    this.vm.form.addControl('settingsForm', this.vm.settingsForm);

    this.vm.settingsForm.get('registrationAutoVoid')?.disable();
    this.vm.settingsForm.get('renewalAutoVoid')?.disable();

    this.setupPreviewListeners();
    this.setupAutoVoidListeners();
  };

  private setupAutoVoidListeners = () => {
    this.vm.settingsForm
      .get('registrationEnableAutoVoid')
      ?.valueChanges.subscribe(val => {
        this.toggleAutoVoid(WORKFLOW_TYPES.REGISTER as any, val);
      });
    this.vm.settingsForm
      .get('renewalEnableAutoVoid')
      ?.valueChanges.subscribe(val => {
        this.toggleAutoVoid(WORKFLOW_TYPES.RENEWAL as any, val);
      });

    this.vm.settingsForm
      .get('registrationAutoVoid')
      ?.valueChanges.subscribe((v: any) => {
        const wf = this.vm.fullEditingObject?.registerWorkflow as any;
        if (wf?.autoVoidSettings) {
          wf.autoVoidSettings.goBeyondActivity = v?.goBeyondActivity || '';
          wf.autoVoidSettings.daysUntilVoid = v?.daysUntilVoid || 0;
        }
        this.calculateAutoVoidExample(WORKFLOW_TYPES.REGISTER as any);
      });

    this.vm.settingsForm
      .get('renewalAutoVoid')
      ?.valueChanges.subscribe((v: any) => {
        const wf = this.vm.fullEditingObject?.renewWorkflow as any;
        if (wf?.autoVoidSettings) {
          wf.autoVoidSettings.goBeyondActivity = v?.goBeyondActivity || '';
          wf.autoVoidSettings.daysUntilVoid = v?.daysUntilVoid || 0;
        }
        this.calculateAutoVoidExample(WORKFLOW_TYPES.RENEWAL as any);
      });
  };

  toggleAutoVoid = (type: 'register' | 'renewal', isEnabled: boolean) => {
    const workflow =
      type === WORKFLOW_TYPES.REGISTER
        ? this.vm.fullEditingObject?.registerWorkflow
        : this.vm.fullEditingObject?.renewWorkflow;
    const formGroup = this.vm.settingsForm.get(
      type === WORKFLOW_TYPES.REGISTER
        ? 'registrationAutoVoid'
        : 'renewalAutoVoid'
    );

    if (isEnabled) {
      if (workflow && !(workflow as any).autoVoidSettings) {
        (workflow as any).autoVoidSettings = {
          goBeyondActivity: '',
          daysUntilVoid: 0
        };
      }
      formGroup?.enable();

      if (
        type === WORKFLOW_TYPES.REGISTER &&
        this.vm.registrationAutoVoidActivities.length === 0
      ) {
        this.loadWorkflowActivities(WORKFLOW_TYPES.REGISTER as any);
      }
      if (
        type === WORKFLOW_TYPES.RENEWAL &&
        this.vm.renewalAutoVoidActivities.length === 0
      ) {
        this.loadWorkflowActivities(WORKFLOW_TYPES.RENEWAL as any);
      }
    } else {
      if (workflow) (workflow as any).autoVoidSettings = null;
      formGroup?.disable();
      formGroup?.reset();
    }

    this.calculateAutoVoidExample(type);
  };

  loadWorkflowActivities = (type: 'register' | 'renewal') => {
    const workflow =
      type === WORKFLOW_TYPES.REGISTER
        ? this.vm.fullEditingObject?.registerWorkflow
        : this.vm.fullEditingObject?.renewWorkflow;
    if (!workflow) return;

    this._workflowSvc.getWorkflowActivities(workflow).subscribe({
      next: activities => {
        const filtered = (activities || [])
          .filter(a => a.showOnScreen)
          .sort((a, b) => (a.level < b.level ? -1 : 1));
        if (type === WORKFLOW_TYPES.REGISTER) {
          this.vm.registrationAutoVoidActivities = filtered;
        } else {
          this.vm.renewalAutoVoidActivities = filtered;
        }

        this.calculateAutoVoidExample(type);
      }
    });
  };

  calculateAutoVoidExample = (type: 'register' | 'renewal') => {
    const workflow =
      type === WORKFLOW_TYPES.REGISTER
        ? this.vm.fullEditingObject?.registerWorkflow
        : this.vm.fullEditingObject?.renewWorkflow;
    const activities =
      type === WORKFLOW_TYPES.REGISTER
        ? this.vm.registrationAutoVoidActivities
        : this.vm.renewalAutoVoidActivities;

    const av = (workflow as any)?.autoVoidSettings;

    if (av?.goBeyondActivity && av?.daysUntilVoid && activities) {
      const days = av.daysUntilVoid;
      const targetActivityId = av.goBeyondActivity;
      const targetActivity = activities.find(a => a.id === targetActivityId);

      if (targetActivity) {
        const startDate = moment().format('MM/DD/YYYY');
        const targetDate = moment()
          .add(days, 'days')
          .format('MM/DD/YYYY');

        const example = `An Application that starts on ${startDate} and doesn't complete the '${targetActivity.model.screenName}' Activity before the end of the day on ${targetDate} will get automatically voided the night of ${targetDate}.`;

        if (type === WORKFLOW_TYPES.REGISTER)
          this.vm.registrationAutoVoidExample = example;
        else this.vm.renewalAutoVoidExample = example;
        return;
      }
    }

    if (type === WORKFLOW_TYPES.REGISTER)
      this.vm.registrationAutoVoidExample = '';
    else this.vm.renewalAutoVoidExample = '';
  };

  private setupPreviewListeners = () => {
    const calcReg = () =>
      this.calculateFixedNumberPreview(WORKFLOW_TYPES.REGISTER as any);
    this.vm.settingsForm
      .get('registrationStartingNumber')
      ?.valueChanges.subscribe(calcReg);
    this.vm.settingsForm
      .get('registerTotalLength')
      ?.valueChanges.subscribe(calcReg);

    const calcRen = () =>
      this.calculateFixedNumberPreview(WORKFLOW_TYPES.RENEWAL as any);
    this.vm.settingsForm
      .get('renewalStartingNumber')
      ?.valueChanges.subscribe(calcRen);
    this.vm.settingsForm
      .get('renewalTotalLength')
      ?.valueChanges.subscribe(calcRen);
  };

  private setDefaultWorkflowTemplatesForNewMode = () => {
    if (this.vm.isEditMode || !this.vm.form) return;

    const registerWorkflowId =
      this.vm.registerWorkflowOptions?.[0]?.value?.toString() || null;
    const renewWorkflowId =
      this.vm.renewWorkflowOptions?.[0]?.value?.toString() || null;

    this.vm.form.patchValue({
      registerWorkflowId,
      renewWorkflowId
    });

    if (this.vm.fullEditingObject) {
      this.vm.fullEditingObject.registerWorkflowId = registerWorkflowId as string;
      this.vm.fullEditingObject.renewWorkflowId = renewWorkflowId as string;
    }

    this._ref.detectChanges();
  };

  private loadWorkflows = () => {
    this._contractorSvc.getGlobalContractorWorkflows().subscribe({
      next: workflows => {
        if (workflows) {
          this.vm.registerWorkflowOptions = workflows
            .filter(w => w.workflowType === WorkflowType.ContractorRegistration)
            .map(w => ({ label: w.version.name, value: w.id }));

          this.vm.renewWorkflowOptions = workflows
            .filter(w => w.workflowType === WorkflowType.ContractorRenewal)
            .map(w => ({ label: w.version.name, value: w.id }));
          this.setDefaultWorkflowTemplatesForNewMode();
        }
      },
      error: err => console.error('Error loading workflows', err)
    });
  };

  private initializeNewMode = () => {
    const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
    this.vm.fullEditingObject = ({
      id: EMPTY_GUID,
      clientID: this._contextSvc.client.id,
      client: this._contextSvc.client,
      name: '',
      description: '',
      visibleToPublic: true,
      registerWorkflowId: null,
      renewWorkflowId: null,
      requirements: [],
      registerRequirementActivities: {},
      renewRequirementActivities: {},
      registerActivities: {},
      renewActivities: {},
      contractorTypeTags: [],
      registerWorkflow: {
        clientApplicationNumber: 1,
        fixedClientApplicationNumberLength: 0
      } as any,
      renewWorkflow: {
        clientApplicationNumber: 1,
        fixedClientApplicationNumberLength: 0
      } as any
    } as unknown) as ContractorType;

    this.ensureDefaultRequirementActivities();
  };

  private loadFullContractorType = (id: string) => {
    this.vm.loadingData = true;
    this.vm.form.disable();

    this._contractorSvc.getContractorType(id, true).subscribe({
      next: fullType => {
        this._securitySvc.getSecurableActions(fullType as any).subscribe({
          next: actions => {
            (fullType as any).availableActions = Array.isArray(actions)
              ? actions
              : actions
              ? Object.keys(actions)
              : [];

            this.vm.fullEditingObject = fullType;

            this.vm.contractorTypeTags = (
              (fullType as any).contractorTypeTags || []
            ).map((ct: any) => {
              return <ItemTag>{
                itemId: ct.contractorTypeId,
                tagId: ct.tagId,
                tag: ct.tag
              };
            });

            this.ensureDefaultRequirementActivities();

            this.vm.form.enable();
            this.vm.form.get('registerWorkflowId')?.disable();
            this.vm.form.get('renewWorkflowId')?.disable();

            this.patchFormValues(fullType);
            this.setWorkflowCategories(fullType);

            this.runValidation();

            if ((fullType as any).registerWorkflow?.autoVoidSettings) {
              this.vm.settingsForm
                .get('registrationAutoVoid')
                ?.patchValue(
                  (fullType as any).registerWorkflow.autoVoidSettings
                );
              this.loadWorkflowActivities(WORKFLOW_TYPES.REGISTER as any);
            }
            if ((fullType as any).renewWorkflow?.autoVoidSettings) {
              this.vm.settingsForm
                .get('renewalAutoVoid')
                ?.patchValue((fullType as any).renewWorkflow.autoVoidSettings);
              this.loadWorkflowActivities(WORKFLOW_TYPES.RENEWAL as any);
            }

            this.sortRequirements();
            this.loadRequirementEntities();

            this.vm.loadingData = false;

            this.onExpirationChange(
              this.vm.form.get('expirationOption')?.value
            );
            this.toggleAutoReset(WORKFLOW_TYPES.REGISTER as any);
            this.toggleAutoReset(WORKFLOW_TYPES.RENEWAL as any);

            this._ref.detectChanges();
          }
        });
      },
      error: err => {
        console.error('Failed to load details', err);
        this.vm.loadingData = false;
        this.vm.form.enable();
      }
    });
  };

  private patchFormValues = (type: ContractorType) => {
    let expOption: any = EXPIRATION_MODES.INFINITE;
    let termLen: number | null = null;

    if (
      (type as any).expirationFormulaCriteria &&
      (type as any).expirationFormulaCriteria.length > 0
    ) {
      const criteria = (type as any)
        .expirationFormulaCriteria as ConditionTarget[];

      if (
        criteria.some(
          c =>
            c.id === '196B539E-B330-4E4B-B577-A850D73B13AA' ||
            (c.value && c.value.includes('DATE(YEAR'))
        )
      ) {
        expOption = EXPIRATION_MODES.END_OF_YEAR;
      } else if (
        criteria.some(
          c =>
            c.id === '475A27B0-1AD9-4840-8E63-BB290D979F6C' ||
            (c.value && c.value.includes('EDATE'))
        )
      ) {
        expOption = EXPIRATION_MODES.CUSTOM_MONTHS;
        const valStr = criteria[0].value || '';
        const matches = valStr.match(/-?\d+/g);
        if (matches && matches.length > 0)
          termLen = parseInt(matches[matches.length - 1], 10);
      } else {
        expOption = 'custom-formula';
        (this.vm as any).customExpirationCriteria = criteria;
      }
    }

    this.vm.selectedExpirationValue = expOption;

    this.vm.form.patchValue({
      name: type.name,
      description: type.description,
      registerWorkflowId: this.vm.isEditMode
        ? (type as any).registerWorkflow?.parentWorkflowid
        : (type as any).registerWorkflowId,

      renewWorkflowId: this.vm.isEditMode
        ? (type as any).renewWorkflow?.parentWorkflowid
        : (type as any).renewWorkflowId,
      visibleToPublic: (type as any).visibleToPublic,
      expirationOption: expOption,
      termLength: termLen
    });

    if ((type as any).registerWorkflow) {
      const rw: any = (type as any).registerWorkflow;
      this.vm.settingsForm.patchValue({
        registrationStartingNumber: rw.clientApplicationNumber,
        registerTotalLength: rw.fixedClientApplicationNumberLength,
        registerAutoReset: !!rw.resetClientAppNumberMonth,
        registerResetMonth: rw.resetClientAppNumberMonth,
        registerResetDay: rw.resetClientAppNumberDay,
        registrationEnableAutoVoid: !!rw.autoVoidSettings,
        registerAppDescription:
          (rw as any).version?.applicationDescriptionTemplate ||
          (rw as any).applicationDescriptionTemplate ||
          ''
      });
      this.calculateFixedNumberPreview(WORKFLOW_TYPES.REGISTER as any);
    }

    if ((type as any).renewWorkflow) {
      const rew: any = (type as any).renewWorkflow;
      this.vm.settingsForm.patchValue({
        renewalStartingNumber: rew.clientApplicationNumber,
        renewalTotalLength: rew.fixedClientApplicationNumberLength,
        renewalAutoReset: !!rew.resetClientAppNumberMonth,
        renewalResetMonth: rew.resetClientAppNumberMonth,
        renewalResetDay: rew.resetClientAppNumberDay,
        renewalEnableAutoVoid: !!rew.autoVoidSettings,
        renewalAppDescription:
          (rew as any).version?.applicationDescriptionTemplate ||
          (rew as any).applicationDescriptionTemplate ||
          ''
      });
      this.calculateFixedNumberPreview(WORKFLOW_TYPES.RENEWAL as any);
    }
  };

  private setWorkflowCategories = (type: ContractorType) => {
    const registerCategories = (
      (type as any).registerWorkflow?.categories || []
    ).filter((category: WorkflowCategory) => !!category);

    const renewalCategories = (
      (type as any).renewWorkflow?.categories || []
    ).filter((category: WorkflowCategory) => !!category);

    this.registerWorkflowCategories = registerCategories;
    this.renewalWorkflowCategories = renewalCategories;

    this.registerCategoriesEnabled = registerCategories.length > 0;
    this.renewalCategoriesEnabled = renewalCategories.length > 0;
  };

  onRegisterCategoriesChanged = (categories: WorkflowCategory[]) => {
    this.registerWorkflowCategories = (categories || []).filter(
      category => !!category
    );
    this.registerCategoriesEnabled = this.registerWorkflowCategories.length > 0;

    this._ref.detectChanges();
  };

  onRegisterCategoriesEnabledChanged = (enabled: boolean) => {
    this.registerCategoriesEnabled = enabled;

    if (!enabled) {
      this.registerWorkflowCategories = [];
    }

    this._ref.detectChanges();
  };

  onRenewalCategoriesChanged = (categories: WorkflowCategory[]) => {
    this.renewalWorkflowCategories = (categories || []).filter(
      category => !!category
    );
    this.renewalCategoriesEnabled = this.renewalWorkflowCategories.length > 0;

    this._ref.detectChanges();
  };

  onRenewalCategoriesEnabledChanged = (enabled: boolean) => {
    this.renewalCategoriesEnabled = enabled;

    if (!enabled) {
      this.renewalWorkflowCategories = [];
    }

    this._ref.detectChanges();
  };

  private removeWorkflowCategoryCircularReferences = (contractorType: any) => {
    const cleanWorkflow = (workflow: any) => {
      if (!workflow) return;
      // Workflow categories are saved by wm-workflow-category-list separately.
      // Do not send them inside contractor type save because category.version.workflow
      // creates a circular JSON reference.
      delete workflow.categories;
      delete workflow.workflowCategories;
    };

    cleanWorkflow(contractorType?.registerWorkflow);
    cleanWorkflow(contractorType?.renewWorkflow);
  };

  onExpirationChange = (value: string | number | null) => {
    this.vm.selectedExpirationValue = value as string;

    const termControl = this.vm.form.get('termLength');
    if (
      this.vm.selectedExpirationValue === EXPIRATION_MODES.CUSTOM_MONTHS ||
      this.vm.selectedExpirationValue === 'custom-months'
    ) {
      termControl?.setValidators([Validators.required, Validators.min(1)]);
      termControl?.enable();
    } else {
      termControl?.clearValidators();
      termControl?.setValue(null);
      termControl?.disable();
    }
    termControl?.updateValueAndValidity();
  };

  updateExpirationCriteria = (criteria: ConditionTarget[]) => {
    (this.vm as any).customExpirationCriteria = criteria || [];
    this._ref.detectChanges();
  };

  calculateFixedNumberPreview = (type: 'register' | 'renewal') => {
    const prefix =
      type === WORKFLOW_TYPES.REGISTER ? 'registration' : 'renewal';
    const totalKey =
      type === WORKFLOW_TYPES.REGISTER
        ? 'registerTotalLength'
        : 'renewalTotalLength';
    const num = this.vm.settingsForm.get(`${prefix}StartingNumber`)?.value || 0;
    const len = this.vm.settingsForm.get(totalKey)?.value || 0;
    const preview = num.toString().padStart(len, '0');
    if (type === WORKFLOW_TYPES.REGISTER)
      this.vm.registerFixedNumberPreview = preview;
    else this.vm.renewalFixedNumberPreview = preview;
  };

  toggleAutoReset = (type: 'register' | 'renewal') => {
    const isEnabled = this.vm.settingsForm.get(`${type}AutoReset`)?.value;
    const monthCtrl = this.vm.settingsForm.get(`${type}ResetMonth`);
    const dayCtrl = this.vm.settingsForm.get(`${type}ResetDay`);
    if (isEnabled) {
      monthCtrl?.enable();
      dayCtrl?.enable();
    } else {
      monthCtrl?.disable();
      dayCtrl?.disable();
      monthCtrl?.setValue(null);
      dayCtrl?.setValue(null);
    }
  };

  onEntitySelected = (entity: string, targetControlName: string) => {
    if (!entity) return;
    const control = this.vm.settingsForm.get(targetControlName);
    if (control) {
      const currentValue = control.value || '';
      const newValue = currentValue + `\${${entity}}`;
      control.setValue(newValue);
      control.markAsDirty();
    }
  };

  getThenLabel = (clause: ConditionTarget): string =>
    clause ? clause.value : 'needs refactored';

  updateCriteria = (
    criteria: ConditionTarget[],
    workflow: any,
    controlName: string
  ) => {
    if (!workflow) return;
    workflow.numberPrefixCriteria = criteria;

    let hasValue = false;
    if (criteria && criteria.length > 0) {
      criteria.forEach(c => {
        if ((c.value || '') !== '') hasValue = true;
      });
    }

    this.vm.settingsForm.controls[controlName].setValue(hasValue ? 'done' : '');
    this.vm.settingsForm.controls[controlName].markAsDirty();
  };

  getValues = (dict: any): any[] => {
    if (!dict) return [];
    return Object.keys(dict)
      .map(k => dict[k])
      .filter(v => typeof v === 'object' && v !== null && v.model);
  };

  tagAdded = (tag: Tag) => {
    const contractorTypeId = this.route.snapshot.paramMap.get(
      'contractorTypeId'
    );
    if (!tag?.id || !contractorTypeId) return;

    const addTagLocal = () => {
      if (!this.vm.contractorTypeTags) this.vm.contractorTypeTags = [];
      if (!this.vm.fullEditingObject.contractorTypeTags)
        this.vm.fullEditingObject.contractorTypeTags = [];

      const exists = this.vm.contractorTypeTags.some(t => t.tagId === tag.id);
      if (exists) return;

      this.vm.contractorTypeTags.push(<ItemTag>{
        itemId: contractorTypeId,
        tagId: tag.id,
        tag
      });

      this.vm.fullEditingObject.contractorTypeTags.push(<ContractorTypeTag>{
        contractorTypeId,
        tagId: tag.id,
        tag
      });

      this._ref.detectChanges();
    };

    this._systemSvc
      .addContractorTypeTag(<ContractorTypeTag>{
        contractorTypeId,
        tagId: tag.id
      })
      .subscribe({
        next: () => addTagLocal(),
        error: err => {
          console.error('❌ addContractorTypeTag failed:', err);
          this.toastError(err, 'Unable to add tag. Please try again.');
        }
      });
  };

  tagRemoved = (tag: Tag) => {
    const contractorTypeId = this.route.snapshot.paramMap.get(
      'contractorTypeId'
    );
    if (!tag?.id || !contractorTypeId) return;

    const removeTagLocal = () => {
      const idx = (this.vm.contractorTypeTags || []).findIndex(
        t => t.tagId === tag.id
      );
      if (idx > -1) this.vm.contractorTypeTags.splice(idx, 1);

      const idx2 = (
        this.vm.fullEditingObject?.contractorTypeTags || []
      ).findIndex((t: any) => t.tagId === tag.id);
      if (idx2 > -1)
        this.vm.fullEditingObject.contractorTypeTags.splice(idx2, 1);

      this._ref.detectChanges();
    };

    this._systemSvc
      .removeContractorTypeTag(<ContractorTypeTag>{
        contractorTypeId,
        tagId: tag.id
      })
      .subscribe({
        next: () => removeTagLocal(),
        error: err => {
          console.error('❌ removeContractorTypeTag failed:', err);
          this.toastError(err, 'Unable to remove tag. Please try again.');
        }
      });
  };

  validateRegisterWorkflow() {
    if (!this.vm.fullEditingObject?.registerWorkflowId) return;
    this.openValidationInfo(this.vm.fullEditingObject.registerWorkflowId);
  }

  validateRenewalWorkflow() {
    if (!this.vm.fullEditingObject?.renewWorkflowId) return;
    this.openValidationInfo(this.vm.fullEditingObject.renewWorkflowId);
  }

  async validateWorkflow(workflowId: string): Promise<any[]> {
    return this._workflowValidation.validate(workflowId);
  }

  async openValidationInfo(workflowId: string) {
    const validity = await this.validateWorkflow(workflowId);

    const modalRef = this.modalService.open(WorkflowValidityModalComponent, {
      ariaLabelledBy: 'modal-validity-title',
      size: 'lg'
    });

    (modalRef.componentInstance as any).validity = validity;
    (modalRef.componentInstance as any).workflowId = workflowId;
  }

  async runValidation() {
    if (!this.vm.fullEditingObject?.id) return;

    const regId = this.vm.fullEditingObject.registerWorkflowId;
    const renId = this.vm.fullEditingObject.renewWorkflowId;

    if (!regId || !renId) return;

    const [regValidity, renValidity] = await Promise.all([
      this.validateWorkflow(regId),
      this.validateWorkflow(renId)
    ]);

    this.validity.register = regValidity || [];
    this.validity.renewal = renValidity || [];

    this.validationInfo.register = this.computeValidationInfo(
      this.validity.register
    );
    this.validationInfo.renewal = this.computeValidationInfo(
      this.validity.renewal
    );

    this._ref.detectChanges();
  }

  private computeValidationInfo(validityArr: any[]) {
    const hasIssues = Array.isArray(validityArr) && validityArr.length > 0;

    return {
      class: hasIssues ? 'btn-danger' : 'btn-success',
      icon: hasIssues ? 'error' : 'check_circle',
      text: hasIssues
        ? `${validityArr.length} validation issue(s) found`
        : 'Workflow is valid'
    };
  }

  startTestRegistration = () => {
    if (!this.vm.fullEditingObject?.registerWorkflowId) return;
    if (this.validity.register?.length > 0) return;

    this.testRegisterLoading = true;

    (this._workflowSvc as any)
      .startApplication(this.vm.fullEditingObject.registerWorkflowId, true)
      .subscribe({
        next: (res: any) => {
          this.testRegisterLoading = false;
          this.openWorkflowOverlay(res.applicationId);
        },
        error: (e: any) => {
          this.testRegisterLoading = false;
          this.toastError(e, 'Unable to start register workflow test.');
        }
      });
  };

  startTestRenewal = () => {
    if (!this.vm.fullEditingObject?.id) return;
    if (this.validity.renewal?.length > 0) return;

    this.testRenewalLoading = true;

    (this._contractorSvc as any)
      .startRegistrationRenewal(null, this.vm.fullEditingObject.id, true)
      .subscribe({
        next: (res: any) => {
          this.testRenewalLoading = false;
          this.openWorkflowOverlay(res.applicationId);
        },
        error: (e: any) => {
          this.testRenewalLoading = false;
          this.toastError(e, 'Unable to start renewal workflow test.');
        }
      });
  };

  private openWorkflowOverlay(applicationId: string) {
    this.showTestWorkflow = false;
    this._ref.detectChanges();

    const returnUrl = this.router.url;

    this._contextSvc.janusLayout = 'staff';
    this._contextSvc.applicationViewType = 'overlay';
    this._contextSvc.useNewLayout = true;
    this._contextSvc.applicationId = applicationId;
    this._contextSvc.applicationReturnURL = returnUrl;

    this._contextSvc.janusLayout$.emit('staff');
    this._contextSvc.applicationViewType$.emit('overlay');
    this._contextSvc.useNewLayout$.emit(true);
    this._contextSvc.applicationId$.emit(applicationId);

    setTimeout(() => {
      this.showTestWorkflow = true;
      this._ref.detectChanges();

      this.router
        .navigate(['application', applicationId], {
          relativeTo: this.route,
          queryParams: {
            t: Date.now(),
            isTestContractorWorkflow: true,
            returnURL: returnUrl
          }
        })
        .then(success => {
          if (!success) {
            this.showTestWorkflow = false;
            this.toastError(null, 'Unable to open test workflow.');
          }
        });
    });
  }

  private syncWorkflowChildState() {
    this.showTestWorkflow = !!this.route.firstChild?.snapshot.paramMap.get(
      'applicationId'
    );
    this._ref.detectChanges();
  }

  onSave = () => {
    if (this.vm.form.invalid || this.vm.settingsForm.invalid) {
      this.vm.form.markAllAsTouched();
      this.vm.settingsForm.markAllAsTouched();
      return;
    }

    this.vm.saving = true;

    const rawForm = this.vm.form.getRawValue();

    this.vm.fullEditingObject.name = rawForm.name;
    this.vm.fullEditingObject.description = rawForm.description;
    this.vm.fullEditingObject.visibleToPublic = !!rawForm.visibleToPublic;

    if (!this.vm.isEditMode) {
      this.vm.fullEditingObject.registerWorkflowId = rawForm.registerWorkflowId;
      this.vm.fullEditingObject.renewWorkflowId = rawForm.renewWorkflowId;
    }

    const settings = this.vm.settingsForm.getRawValue();

    const mapWorkflowSettings = (
      workflow: any,
      type: 'register' | 'renewal'
    ) => {
      if (!workflow) return;

      const prefixKey =
        type === WORKFLOW_TYPES.REGISTER ? 'registration' : 'renewal';
      const shortKey = type;

      workflow.clientApplicationNumber = settings[`${prefixKey}StartingNumber`];
      workflow.fixedClientApplicationNumberLength =
        settings[`${shortKey}TotalLength`];
      workflow.resetClientAppNumberMonth = settings[`${shortKey}AutoReset`]
        ? settings[`${shortKey}ResetMonth`]
        : null;
      workflow.resetClientAppNumberDay = settings[`${shortKey}AutoReset`]
        ? settings[`${shortKey}ResetDay`]
        : null;

      if (!workflow.version) {
        workflow.version = {};
      }

      workflow.version.applicationDescriptionTemplate =
        settings[`${shortKey}AppDescription`];

      if (settings[`${prefixKey}EnableAutoVoid`]) {
        if (!workflow.autoVoidSettings) {
          workflow.autoVoidSettings = {
            daysUntilVoid: 30,
            goBeyondActivity: ''
          };
        }

        const autoVoidForm = settings[`${prefixKey}AutoVoid`];
        if (autoVoidForm) {
          workflow.autoVoidSettings.goBeyondActivity =
            autoVoidForm.goBeyondActivity;
          workflow.autoVoidSettings.daysUntilVoid = autoVoidForm.daysUntilVoid;
        }
      } else {
        workflow.autoVoidSettings = null;
      }
    };

    mapWorkflowSettings(
      this.vm.fullEditingObject.registerWorkflow,
      WORKFLOW_TYPES.REGISTER as any
    );
    mapWorkflowSettings(
      this.vm.fullEditingObject.renewWorkflow,
      WORKFLOW_TYPES.RENEWAL as any
    );

    const expOption = this.vm.form.get('expirationOption')?.value;

    if (
      expOption === EXPIRATION_MODES.END_OF_YEAR ||
      expOption === 'end-of-year'
    ) {
      this.vm.fullEditingObject.expirationFormulaCriteria = [
        new ConditionTarget({
          id: '196B539E-B330-4E4B-B577-A850D73B13AA',
          value: 'DATE(YEAR(${EFFECTIVE_DATE}),12,31)',
          conditionTargetType: 'Formula',
          group: {
            id: Utilities.generateId(),
            formulaOperator: '==',
            isGroup: true,
            conditions: []
          }
        } as any)
      ];
    } else if (
      expOption === EXPIRATION_MODES.CUSTOM_MONTHS ||
      expOption === 'custom-months'
    ) {
      const termLen = this.vm.form.get('termLength')?.value || 0;

      this.vm.fullEditingObject.expirationFormulaCriteria = [
        new ConditionTarget({
          id: '475A27B0-1AD9-4840-8E63-BB290D979F6C',
          value: `EDATE(\${EFFECTIVE_DATE}, ${termLen})`,
          conditionTargetType: 'Formula',
          group: {
            id: Utilities.generateId(),
            formulaOperator: '==',
            isGroup: true,
            conditions: []
          }
        } as any)
      ];
    } else if (expOption === 'custom-formula') {
      this.vm.fullEditingObject.expirationFormulaCriteria =
        (this.vm as any).customExpirationCriteria || [];
    } else {
      this.vm.fullEditingObject.expirationFormulaCriteria = null;
    }

    this.vm.fullEditingObject.isPublished = false;

    this.removeWorkflowCategoryCircularReferences(this.vm.fullEditingObject);

    // delete (this.vm.fullEditingObject as any).client;

    this._contractorSvc
      .saveContractorType(this.vm.fullEditingObject)
      .subscribe({
        next: () => {
          this.vm.saving = false;

          this.toastSuccess(
            this.vm.isEditMode
              ? 'Contractor Type updated successfully.'
              : 'Contractor Type created successfully.'
          );

          if ((this._contractorSvc as any)['refreshList$']) {
            (this._contractorSvc as any)['refreshList$'].next();
          }

          this.closeWithRefresh();
        },
        error: err => {
          this.vm.saving = false;
          console.error('Save failed:', err);
          this.toastError(
            err,
            'Unable to save Contractor Type. Please try again.'
          );
        }
      });
  };

  onClose = () => {
    this.updateOverlayOffset();
    if (this.route.firstChild) {
      this.showTestWorkflow = false;
      this.router
        .navigate(['../../'], {
          relativeTo: this.route.firstChild
        })
        .then(() => {
          this.showTestWorkflow = false;
          this._ref.detectChanges();
        });

      return;
    }

    const isEditMode = this.route.snapshot.paramMap.get('contractorTypeId');

    if (isEditMode) {
      this.router.navigate(['../../'], { relativeTo: this.route });
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
  };

  closeWithRefresh = () => {
    const isEditMode = this.route.snapshot.paramMap.get('contractorTypeId');
    const navigationExtras = {
      relativeTo: this.route,
      queryParams: { refresh: new Date().getTime() }
    };

    if (isEditMode) {
      this.router.navigate(['../../'], navigationExtras);
    } else {
      this.router.navigate(['../'], navigationExtras);
    }
  };
  private overlayBaseTop: number | null = null;
  // Added by priya on 05-10-2026
//    * Calculates the overlay position and available height based on the current
//  * visible scroll area. For Edit, the first visible card position is used to
//  * keep the overlay within the viewport. For Add, a fixed top offset is used.

private updateOverlayOffset(): void {
  const navigationState =
    this.router.getCurrentNavigation()?.extras?.state ||
    window.history.state;
  const selectedRowTop = Number(
    navigationState?.overlayRowTop
  );
const isNewContractorType =
  navigationState?.isNewContractorType === true;
  if (isNewContractorType) {
  this.overlayOffsetTop = 20;
  this.overlayBaseTop = null;

  const calculatedHeight = Math.min(
    this.defaultOverlayHeight,
    window.innerHeight - this.overlayBottomSpacing
  );

  this.overlayAvailableHeight = `${calculatedHeight}px`;

  return;
}



  const overlayElement = document.querySelector(
    'wm-common-overlay-modal.contractor-type-overlay'
  ) as HTMLElement | null;

  if (
    !overlayElement ||
    !Number.isFinite(selectedRowTop)
  ) {
    this.overlayOffsetTop = 0;
    this.overlayBaseTop = null;

    this.overlayAvailableHeight = `
      min(
        ${this.defaultOverlayHeight}px,
        calc(100vh - 48px)
      )
    `;

    return;
  }


  /*
   * Capture the overlay's original position only once.
   * Do NOT capture the position after our offset has been applied.
   */
  if (this.overlayBaseTop === null) {
    const overlayRect =
      overlayElement.getBoundingClientRect();

    this.overlayBaseTop =
      overlayRect.top - this.overlayOffsetTop;

   
  }

  /*
 * Position the Edit overlay relative to the first visible card
 * so it opens within the currently visible scroll area.
 */

  const calculatedTop =
    selectedRowTop - this.overlayBaseTop;


  const overlayTopSpacing = 10;

  this.overlayOffsetTop =
    Math.max(
      0,
      calculatedTop + overlayTopSpacing
    );

  /*
   * Keep the overlay height based on the viewport,
   * not on the selected card position.
   */
  const calculatedHeight = Math.min(
    this.defaultOverlayHeight,
    window.innerHeight - this.overlayBottomSpacing
  );

  this.overlayAvailableHeight =
    `${calculatedHeight}px`;
}
}
